import { useCallback, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useIsOrganizationOwner } from '@modules-deprecated/app/organization/hooks/useIsOrganizationOwner'

import { useOrganizationMarket, useOrganizationSubscription } from '../../../hooks'
import type { BackToSchoolAudience } from '../types/backToSchoolAudience'
import { getBackToSchoolAudience } from '../utils/getBackToSchoolAudience'
import { getBackToSchoolRemainingDays } from '../utils/getBackToSchoolRemainingDays'
import { useTrackBackToSchoolCampaign } from './useTrackBackToSchoolCampaign'

interface TrackCampaignEntryPointParams {
  ctaLabel?: string
  entryPoint: string
}

interface UseBackToSchoolCampaignResult {
  audience: BackToSchoolAudience
  isCampaignActive: boolean
  isLoading: boolean
  remainingCampaignDays: number | null
  remainingTrialDays: number | null
  trackCampaignEntryPointClicked: (params: TrackCampaignEntryPointParams) => void
  trackCampaignEntryPointViewed: (params: TrackCampaignEntryPointParams) => void
}

export const useBackToSchoolCampaign = (): UseBackToSchoolCampaignResult => {
  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { isLoading: isLoadingOrganizationOwner, isOrganizationOwner } = useIsOrganizationOwner()
  const { market } = useOrganizationMarket()
  const {
    isLoading: isLoadingSubscription,
    isLocked,
    isTrialNoPlanSelected,
    remainingTrialDays,
    subscriptionPlan,
  } = useOrganizationSubscription()
  const { trackCampaign } = useTrackBackToSchoolCampaign()

  const isLoading = isLoadingSubscription || isLoadingOrganization || isLoadingOrganizationOwner
  const isExpiredTrial = organization?.lockedCode === 'expiredTrial'
  const remainingDays = getBackToSchoolRemainingDays()
  const remainingCampaignDays = remainingDays > 0 ? remainingDays : null

  const audience = useMemo(
    () =>
      getBackToSchoolAudience({
        consolidatedBilling: !!organization?.consolidatedBilling,
        isExpiredTrial,
        isLocked,
        isLoading,
        isOrganizationOwner,
        isTrialNoPlanSelected,
        market,
        remainingCampaignDays: remainingDays,
        subscriptionPlan,
      }),
    [
      organization?.consolidatedBilling,
      isExpiredTrial,
      isLocked,
      isLoading,
      isOrganizationOwner,
      isTrialNoPlanSelected,
      market,
      remainingDays,
      subscriptionPlan,
    ],
  )

  const trackCampaignEntryPointClicked = useCallback(
    ({ ctaLabel, entryPoint }: TrackCampaignEntryPointParams) => {
      if (!audience) {
        return
      }

      trackCampaign('Entry Point Clicked', {
        ctaLabel,
        daysRemaining: remainingCampaignDays,
        entryPoint,
        targetSegment: audience,
      })
    },
    [audience, remainingCampaignDays, trackCampaign],
  )

  const trackCampaignEntryPointViewed = useCallback(
    ({ entryPoint }: TrackCampaignEntryPointParams) => {
      if (!audience) {
        return
      }

      trackCampaign('Entry Point Viewed', {
        daysRemaining: remainingCampaignDays,
        entryPoint,
        targetSegment: audience,
      })
    },
    [audience, remainingCampaignDays, trackCampaign],
  )

  return {
    audience,
    isCampaignActive: audience !== null,
    isLoading,
    remainingCampaignDays,
    remainingTrialDays,
    trackCampaignEntryPointClicked,
    trackCampaignEntryPointViewed,
  }
}
